<div class="py-4">
  <!-- "ทักษะของฉัน" is wrong when a parent is reading their child's report —
       the component already knows, because the child had to be named in the
       URL for the API to answer at all. -->
  <h2 class="fs-3 fw-bold mb-5" data-testid="skills-heading">
    {{ (viewingOther() ? "QUIZ.CHILD_SKILLS" : "QUIZ.MY_SKILLS") | translate }}
  </h2>

  @if (isLoading()) {
  <div data-testid="skills-loading">
    <div class="skeleton h-125px w-100 mb-6"></div>
    <div class="row g-4 mb-6">
      <div class="col-6 col-lg-3">
        <div class="skeleton h-90px w-100"></div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="skeleton h-90px w-100"></div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="skeleton h-90px w-100"></div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="skeleton h-90px w-100"></div>
      </div>
    </div>
    <ng-container *ngTemplateOutlet="skillsSectionSkeleton"></ng-container>
    <ng-container *ngTemplateOutlet="skillsSectionSkeleton"></ng-container>
  </div>
  } @else if (!profile() || !profile()?.total_answered) {
  <div class="card min-h-50vh" data-testid="skills-empty">
    <div class="card-body d-flex flex-column justify-content-center is-empty">
      <div class="empty-state">
        <p class="fs-5 text-muted mb-4">{{ "QUIZ.NO_SKILL_DATA" | translate }}</p>
        <a class="btn btn-primary" routerLink="/online-class/quiz">{{ "QUIZ.TITLE" | translate }}</a>
      </div>
    </div>
  </div>
  } @else if (profile(); as p) {

  <!-- The paragraph goes FIRST. A wall of percentages is the answer to a
         question nobody asked; "what is going on and what do we do" is the
         question, and it deserves the top of the page. -->
  @if (narrative(); as n) {
  <!-- Ink, not the brand gradient. This paragraph is the most-read text on
           the page and it was set in white on #53bdee — about 2:1, the one
           pairing this palette cannot carry. On ink the same copy measures
           13:1 and the panel reads as the site's own voice. -->
  <div class="card mb-6 border-0 section-ink skill-narrative" data-testid="skill-narrative">
    <div class="card-body">
      <div class="d-flex align-items-center gap-3 mb-4 flex-wrap">
        <span class="eyebrow"><span class="dot"></span>{{ "QUIZ.SUMMARY" | translate }}</span>
        @if (n.source === "template") {
        <!-- Say plainly when a human-sounding paragraph was assembled
                   rather than written. -->
        <span class="fs-8 text-white opacity-60">{{ "QUIZ.SUMMARY_AUTO" | translate }}</span>
        }
      </div>
      <p class="fs-5 lh-lg mb-0 text-white">{{ n.summary }}</p>
    </div>
  </div>
  }

  <div class="row g-4 mb-6">
    <div class="col-6 col-lg-3">
      <div class="card h-100">
        <div class="card-body is-stat">
          <div class="fs-2hx fw-bold">{{ p.total_answered }}</div>
          <div class="fs-8 text-muted">{{ "QUIZ.ANSWERED" | translate }}</div>
        </div>
      </div>
    </div>
    <div class="col-6 col-lg-3">
      <div class="card h-100">
        <div class="card-body is-stat">
          <div class="fs-2hx fw-bold text-success">{{ p.strong_count }}</div>
          <div class="fs-8 text-muted">{{ "QUIZ.STRONG_TOPICS" | translate }}</div>
        </div>
      </div>
    </div>
    <div class="col-6 col-lg-3">
      <div class="card h-100">
        <div class="card-body is-stat">
          <div class="fs-2hx fw-bold text-danger">{{ p.weak_count }}</div>
          <div class="fs-8 text-muted">{{ "QUIZ.WEAK_TOPICS" | translate }}</div>
        </div>
      </div>
    </div>
    <div class="col-6 col-lg-3">
      <div class="card h-100">
        <div class="card-body is-stat">
          <div class="fs-2hx fw-bold text-primary">{{ p.improving_count }}</div>
          <div class="fs-8 text-muted">{{ "QUIZ.IMPROVING" | translate }}</div>
        </div>
      </div>
    </div>
  </div>

  @if (recommendations().length) {
  <div class="card mb-6">
    <div class="card-header">
      <h3 class="card-title fs-4">{{ "QUIZ.WHAT_NEXT" | translate }}</h3>
    </div>
    <div class="card-body" data-testid="recommendations">
      @for (row of recommendations(); track row.sub_lesson_id) {
      <div class="d-flex flex-column flex-md-row align-items-md-center gap-3 py-4 border-bottom border-gray-200">
        <div class="flex-grow-1">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="fs-5 fw-semibold">{{ row.sub_lesson_name }}</span>
            <!-- Order matters more than urgency here. Drilling a topic
                       that sits on top of a broken one is practice that cannot
                       work, and the student experiences it as being bad at
                       everything instead of missing one thing. -->
            @if (row.is_root_cause) {
            <span class="badge badge-light-primary fs-9">{{ "QUIZ.START_HERE" | translate }}</span>
            }
          </div>
          @if (row.lesson_name) { <div class="fs-8 text-muted">{{ row.lesson_name }}</div> }
          <div class="fs-7 text-muted mt-1">{{ row.reason }}</div>
          @if (row.blocked_by?.length) {
          <div class="fs-8 text-warning mt-1" data-testid="blocked-by">
            {{ "QUIZ.NEEDS_FIRST" | translate }}
            {{ blockedNames(row) }}
          </div>
          }
        </div>
        <div class="d-flex gap-2 flex-shrink-0">
          @if (row.watch_chapter_id) {
          <a class="btn btn-sm btn-light" [routerLink]="['/online-class/chapter', row.watch_chapter_id]">
            {{ "QUIZ.REWATCH" | translate }}
          </a>
          }
          @if (row.practice_available > 0) {
          <button class="btn btn-sm btn-primary" type="button" data-testid="practice-topic"
            [disabled]="buildingFor() !== null" (click)="practise(row)">
            {{ buildingFor() === row.sub_lesson_id
            ? ("QUIZ.BUILDING" | translate)
            : ("QUIZ.PRACTISE" | translate) }}
          </button>
          }
        </div>
      </div>
      }
    </div>
  </div>
  }

  <!-- The same mistake, more than once.
         A per-topic percentage cannot show this: a student who mishandles
         decimal places reads as moderately weak at four topics rather than
         clearly wrong about one thing they could fix this afternoon. -->
  @if (p.misconceptions?.length) {
  <div class="card mb-6">
    <div class="card-header">
      <h3 class="card-title fs-4">{{ "QUIZ.REPEATED_MISTAKES" | translate }}</h3>
    </div>
    <div class="card-body" data-testid="misconceptions">
      <p class="fs-7 text-muted mb-4">{{ "QUIZ.REPEATED_MISTAKES_HINT" | translate }}</p>
      @for (row of p.misconceptions; track row.misconception) {
      <div class="d-flex align-items-start gap-3 py-3 border-bottom border-gray-200">
        <span class="badge badge-light-danger flex-shrink-0">×{{ row.count }}</span>
        <div class="flex-grow-1">
          <div class="fs-6">{{ row.misconception }}</div>
          <div class="fs-8 text-muted mt-1">{{ row.sub_lesson_names.join(" · ") }}</div>
        </div>
      </div>
      }
    </div>
  </div>
  }

  <!-- What they DID learn and have not touched since.
         Not weakness — the opposite. A system that only ever chases weak topics
         quietly teaches students to abandon everything they have already
         learned; this is the other half of the same model. -->
  @if (p.review_queue?.length) {
  <div class="card mb-6">
    <div class="card-header">
      <h3 class="card-title fs-4">{{ "QUIZ.REVIEW_DUE" | translate }}</h3>
    </div>
    <div class="card-body" data-testid="review-queue">
      <p class="fs-7 text-muted mb-4">{{ "QUIZ.REVIEW_DUE_HINT" | translate }}</p>
      @for (row of p.review_queue; track row.sub_lesson_id) {
      <div class="d-flex flex-column flex-sm-row align-items-sm-center gap-3 py-3 border-bottom border-gray-200">
        <div class="flex-grow-1">
          <div class="fs-6 fw-semibold">{{ row.sub_lesson_name }}</div>
          <div class="fs-8 text-muted mt-1">{{ row.reason }}</div>
        </div>
        @if (row.practice_available > 0 && !viewingOther()) {
        <button class="btn btn-sm btn-light-primary flex-shrink-0" data-testid="practice-review"
          [disabled]="buildingFor() === row.sub_lesson_id" (click)="practiseTopic(row.sub_lesson_id)">
          {{ (buildingFor() === row.sub_lesson_id ? "QUIZ.BUILDING" : "QUIZ.PRACTISE") | translate }}
        </button>
        }
      </div>
      }
    </div>
  </div>
  }

  @if (suggestions().length) {
  <div class="card mb-6">
    <div class="card-header">
      <h3 class="card-title fs-4">{{ "QUIZ.SUGGESTED_COURSES" | translate }}</h3>
    </div>
    <div class="card-body" data-testid="course-suggestions">
      @for (course of suggestions(); track course.course_id) {
      <div class="d-flex flex-column flex-md-row align-items-md-center gap-3 py-4 border-bottom border-gray-200">
        <div class="flex-grow-1">
          <div class="fs-5 fw-semibold">{{ course.course_name }}</div>
          <!-- The reason comes from the server, built from the topics it
                     actually matched. A recommendation attached to a price has
                     to be able to show its working. -->
          <div class="fs-7 text-muted mt-1">{{ course.reason }}</div>
        </div>
        <div class="flex-shrink-0">
          @if (course.already_enrolled) {
          <a class="btn btn-sm btn-light-success" routerLink="/online-class">
            {{ "QUIZ.ALREADY_ENROLLED" | translate }}
          </a>
          } @else {
          <a class="btn btn-sm btn-light-primary" [routerLink]="['/courses', course.course_id]">
            {{ "QUIZ.VIEW_COURSE" | translate }}
          </a>
          }
        </div>
      </div>
      }
    </div>
  </div>
  }

  @for (lesson of p.lessons; track lesson.lesson_name) {
  <div class="card mb-4">
    <div class="card-header">
      <h3 class="card-title fs-5">{{ lesson.lesson_name }}</h3>
      <div class="card-toolbar">
        <!-- A bar, like the result page. The pill said the number and the
                 verdict in the same mark, which meant a reader who cannot
                 separate the colours was left with the number alone and no
                 sense of scale. -->
        <span class="skill-meter">
          <span class="skill-meter__track">
            <span class="skill-meter__fill" [ngClass]="masteryBand(lesson.mastery)"
              [style.width.%]="lesson.percent"></span>
          </span>
          <span class="skill-meter__value">{{ lesson.percent }}%</span>
        </span>
      </div>
    </div>
    <div class="card-body">
      @for (row of lesson.sub_lessons; track row.sub_lesson_id) {
      <div class="d-flex align-items-center gap-3 py-3 border-bottom border-gray-200">
        <span class="flex-grow-1 fs-6">{{ row.sub_lesson_name }}</span>
        <span class="fs-8 text-muted">{{ row.correct }}/{{ row.attempted }}</span>
        <!-- Percent says how the papers went; this says what the student
                   can DO, on the same scale the questions are labelled with —
                   so "อยู่ระดับ ยาก" and "ข้อนี้ระดับ ยาก" mean an even chance.
                   The range is one standard error either side: when the two
                   ends differ, saying only the middle would be a false
                   precision. Shown only once there is evidence to mean it. -->
        @if (row.ability?.confident && row.ability; as ability) {
        <span class="fs-8 text-primary d-none d-sm-inline" [attr.title]="'QUIZ.LEVEL_HINT' | translate"
          data-testid="ability">
          {{ "QUIZ.LEVEL" | translate }}
          @if (ability.level_low === ability.level_high) {
          {{ ability.level_label }}
          } @else {
          {{ "QUIZ.LEVEL_RANGE" | translate }} {{ levelLabel(ability.level_low) }}–{{ levelLabel(ability.level_high) }}
          }
        </span>
        }
        <!-- Recency matters more than the lifetime average: a topic just
                   fixed should read as fixed. -->
        <span class="fs-7" [class.text-success]="row.trend === 'up'" [class.text-danger]="row.trend === 'down'"
          [class.text-muted]="row.trend === 'steady'">{{ trendIcon(row.trend) }}</span>
        <!-- The badge keeps saying what was true when it was measured —
                   a child must not appear to get worse at something they simply
                   have not touched — and this says the measurement has expired. -->
        @if (row.stale) {
        <span class="badge badge-light flex-shrink-0 d-none d-sm-inline" data-testid="stale-badge">
          {{ "QUIZ.REVIEW_BADGE" | translate }}
        </span>
        }
        <span class="skill-meter flex-shrink-0">
          <span class="skill-meter__track">
            <span class="skill-meter__fill" [ngClass]="masteryBand(row.mastery)"
              [style.width.%]="row.judged_percent"></span>
          </span>
          <span class="skill-meter__value">{{ row.judged_percent }}%</span>
        </span>
      </div>
      <!-- Where inside the topic it breaks down. A single percentage
                 cannot tell "fine until it gets hard" apart from "shaky all the
                 way through", and those need different help. -->
      @if (row.by_difficulty?.length) {
      <div class="d-flex flex-wrap gap-2 pb-3 ps-1" data-testid="by-level">
        <span class="fs-8 text-muted">{{ "QUIZ.BY_LEVEL" | translate }}:</span>
        @for (slice of row.by_difficulty; track slice.difficulty) {
        <span class="badge badge-sm" [ngClass]="masteryClass(sliceMastery(slice.percent))">
          {{ slice.difficulty_label }} {{ slice.correct }}/{{ slice.attempted }}
        </span>
        }
      </div>
      }
      }
    </div>
  </div>
  }
  }
</div>

<ng-template #skillsSectionSkeleton>
  <div class="card mb-6">
    <div class="card-header align-items-center">
      <div class="skeleton h-25px w-175px"></div>
    </div>
    <div class="card-body pt-9">
      <div class="skeleton h-20px w-100 mb-4"></div>
      <div class="skeleton h-20px w-100 mb-4"></div>
      <div class="skeleton h-20px w-75"></div>
    </div>
  </div>
</ng-template>